import { requireBrowserOrigin } from "@airange/shared/browser";
import { resolveIAiApiBaseUrl, resolveIAiGeneratedBrowserPublicServiceBaseUrl } from "@airange/shared/platform-urls";

const EXAM_API_BASE_ENV_KEY = "UMI_APP_EXAM_API_BASE_URL";
const SSO_OIDC_SCOPE_ENV_KEY = "UMI_APP_SSO_OIDC_SCOPE";

type WindowLocationLike = {
  hostname: string;
  protocol: string;
  origin: string;
};

export function readClientEnv(key: string) {
  const processEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
  if (typeof processEnv?.[key] === "string" && processEnv[key]?.trim()) {
    return processEnv[key]!.trim();
  }
  return "";
}

function getWindowLocation(): WindowLocationLike | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return window.location;
}

export function normalizeApiBaseUrl(rawValue: string, windowLocation: WindowLocationLike | undefined = getWindowLocation()) {
  return resolveIAiApiBaseUrl(
    rawValue,
    windowLocation?.hostname,
    windowLocation?.protocol,
  );
}

function requireGeneratedServiceUrl(
  service: Parameters<typeof resolveIAiGeneratedBrowserPublicServiceBaseUrl>[0],
  message: string,
) {
  try {
    return resolveIAiGeneratedBrowserPublicServiceBaseUrl(service, {
      hostname: typeof window !== "undefined" ? window.location.hostname : undefined,
      origin: typeof window !== "undefined" ? window.location.origin : undefined,
      readClientEnv,
    }).replace(/\/+$/, "");
  } catch (error) {
    if (error instanceof Error && error.message.startsWith("Cannot resolve ")) {
      throw new Error(message);
    }
    throw error;
  }
}

function readOptionalClientEnv(key: string) {
  return readClientEnv(key) || undefined;
}

export function resolveApiBaseUrl(options?: {
  envApiBaseUrl?: string;
  windowLocation?: WindowLocationLike;
}) {
  const windowLocation = options?.windowLocation ?? getWindowLocation();
  const rawApiBaseUrl = options?.envApiBaseUrl
    ?? readOptionalClientEnv(EXAM_API_BASE_ENV_KEY)
    ?? `${(windowLocation?.origin ?? requireBrowserOrigin(
      "Admin API base URL requires explicit env or a browser origin",
    )).replace(/\/+$/, "")}/api`;
  return normalizeApiBaseUrl(rawApiBaseUrl, windowLocation);
}

export const API_BASE_URL = resolveApiBaseUrl();

export const SSO_API_BASE_URL = requireGeneratedServiceUrl(
  "sso-center",
  "Admin SSO base URL requires registry-generated env or a browser host that resolves the sso service",
);

export const SSO_OIDC_SCOPE = readOptionalClientEnv(SSO_OIDC_SCOPE_ENV_KEY)
  ?? "openid profile email groups offline_access";

export const PORTAL_BASE_URL = requireGeneratedServiceUrl(
  "airange-portal",
  "Admin portal base URL requires registry-generated env or a browser host that resolves the portal service",
);

export const CANDIDATE_BASE_URL = requireGeneratedServiceUrl(
  "exam-candidate",
  "Admin candidate base URL requires registry-generated env or a browser host that resolves the candidate service",
);

export const EXAM_ADMIN_BASE_URL = requireGeneratedServiceUrl(
  "exam-admin",
  "Admin exam-admin base URL requires registry-generated env or a browser host that resolves the exam-admin service",
);
